import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { getLicenses, linkLicense, getLatestPosDownload, getBranches, getPOSTerminals } from '../../services/api';
import { KeyRound, Copy, Download, Monitor, Link2, Check, X, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_LABELS = {
  pending: { label: 'Pendiente de activar', color: 'bg-yellow-100 text-yellow-700', desc: 'Instala el POS e ingresa el serial para activar' },
  activated: { label: 'Activada', color: 'bg-blue-100 text-blue-700', desc: 'Liga esta licencia a una sucursal y caja' },
  linked: { label: 'Activa', color: 'bg-green-100 text-green-700', desc: 'POS operando correctamente' },
  expired: { label: 'Expirada', color: 'bg-slate-100 text-slate-600', desc: 'Contacta al administrador para renovar' },
  revoked: { label: 'Revocada', color: 'bg-red-100 text-red-700', desc: 'Esta licencia fue desactivada' },
};

const POS_TYPE_LABELS = { retail: 'POS Retail', restaurant: 'POS Restaurante' };

const LicenciasPOS = () => {
  const { user } = useAuth();
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [terminals, setTerminals] = useState([]);
  const [latestDownloads, setLatestDownloads] = useState({});
  const [showLinkModal, setShowLinkModal] = useState(null);
  const [linkForm, setLinkForm] = useState({ branch_id: '', terminal_id: '' });

  useEffect(() => {
    if (user?.business_id) loadData();
  }, [user]);

  const loadData = async () => {
    try {
      const [licRes, branchRes] = await Promise.all([
        getLicenses(),
        getBranches(user.business_id),
      ]);
      const lics = licRes.data.licenses || [];
      const branchList = branchRes.data;
      setBranches(branchList);

      // Resolve terminal names for linked licenses
      const branchTerminals = {};
      for (const lic of lics) {
        if (lic.status === 'linked' && lic.branch_id && lic.terminal_id) {
          if (!branchTerminals[lic.branch_id]) {
            try {
              const res = await getPOSTerminals(lic.branch_id);
              branchTerminals[lic.branch_id] = res.data;
            } catch { branchTerminals[lic.branch_id] = []; }
          }
          const terminal = branchTerminals[lic.branch_id].find(t => t.id === lic.terminal_id);
          lic._terminal_name = terminal?.name || null;
        }
      }
      setLicenses(lics);

      // Load latest downloads for each type
      const downloads = {};
      for (const type of ['retail', 'restaurant']) {
        try {
          const res = await getLatestPosDownload(type);
          downloads[type] = res.data;
        } catch (e) { /* no version available */ }
      }
      setLatestDownloads(downloads);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar licencias');
    } finally {
      setLoading(false);
    }
  };

  const loadTerminals = async (branchId) => {
    try {
      const res = await getPOSTerminals(branchId);
      setTerminals(res.data);
    } catch (error) {
      setTerminals([]);
    }
  };

  const handleBranchChange = (branchId) => {
    setLinkForm({ ...linkForm, branch_id: branchId, terminal_id: '' });
    if (branchId) loadTerminals(branchId);
    else setTerminals([]);
  };

  const handleLink = async (e) => {
    e.preventDefault();
    if (!showLinkModal) return;
    try {
      await linkLicense(showLinkModal.id, linkForm);
      toast.success('Licencia ligada exitosamente');
      setShowLinkModal(null);
      setLinkForm({ branch_id: '', terminal_id: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al ligar licencia');
    }
  };

  const copySerial = (serial) => {
    navigator.clipboard.writeText(serial);
    toast.success('Serial copiado al portapapeles');
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

  const timeSince = (d) => {
    if (!d) return null;
    const now = new Date();
    const date = new Date(d);
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return 'Hace un momento';
    if (diff < 3600) return `Hace ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `Hace ${Math.floor(diff / 3600)} hrs`;
    return `Hace ${Math.floor(diff / 86400)} días`;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 tracking-tight">Mis Licencias POS</h1>
          <p className="text-slate-600 mt-2">Licencias de POS de escritorio de tu negocio</p>
        </div>

        {licenses.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <KeyRound className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 mb-2">No tienes licencias POS de escritorio</p>
            <p className="text-sm text-slate-400">Contacta al administrador para adquirir una licencia</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {licenses.map((lic) => {
              const st = STATUS_LABELS[lic.status] || { label: lic.status, color: 'bg-slate-100 text-slate-600', desc: '' };
              const dl = latestDownloads[lic.pos_type];
              const lastSeen = timeSince(lic.last_seen);

              return (
                <div key={lic.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  {/* Header */}
                  <div className="p-5 border-b border-slate-100">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Monitor className="w-5 h-5 text-teal-700" />
                        <span className="font-bold text-slate-900">{POS_TYPE_LABELS[lic.pos_type] || lic.pos_type}</span>
                      </div>
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${st.color}`}>{st.label}</span>
                    </div>

                    {/* Serial */}
                    <div className="flex items-center gap-2 bg-slate-50 p-3 rounded-lg">
                      <KeyRound className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <code className="text-sm font-mono text-slate-800 flex-1 select-all">{lic.serial_number}</code>
                      <button onClick={() => copySerial(lic.serial_number)} className="p-1.5 hover:bg-slate-200 rounded transition-colors" title="Copiar serial">
                        <Copy className="w-4 h-4 text-slate-500" />
                      </button>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="p-5 space-y-3">
                    {/* Status hint */}
                    <p className="text-sm text-slate-500">{st.desc}</p>

                    {/* Info */}
                    {lic.status === 'linked' && (
                      <div className="space-y-2 text-sm">
                        {lic.branch_id && (
                          <div className="flex items-center justify-between">
                            <span className="text-slate-500">Sucursal:</span>
                            <span className="font-medium text-slate-900">{branches.find(b => b.id === lic.branch_id)?.name || 'Desconocida'}</span>
                          </div>
                        )}
                        {lic.terminal_id && (
                          <div className="flex items-center justify-between">
                            <span className="text-slate-500">Caja:</span>
                            <span className="font-medium text-slate-900">{lic._terminal_name || lic.terminal_id.slice(0, 8) + '...'}</span>
                          </div>
                        )}
                        {lic.hardware_details?.hostname && (
                          <div className="flex items-center justify-between">
                            <span className="text-slate-500">Equipo:</span>
                            <span className="font-medium text-slate-900">{lic.hardware_details.hostname}</span>
                          </div>
                        )}
                        {lastSeen && (
                          <div className="flex items-center justify-between">
                            <span className="text-slate-500">Última conexión:</span>
                            <span className="font-medium text-slate-900">{lastSeen}</span>
                          </div>
                        )}
                        {lic.pos_version && (
                          <div className="flex items-center justify-between">
                            <span className="text-slate-500">Versión:</span>
                            <span className="font-medium text-slate-900">v{lic.pos_version}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-col gap-2 pt-2">
                      {/* Link button for activated but not linked */}
                      {lic.status === 'activated' && (
                        <button
                          onClick={() => { setShowLinkModal(lic); setLinkForm({ branch_id: '', terminal_id: '' }); setTerminals([]); }}
                          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-teal-700 text-white rounded-lg hover:bg-teal-800 font-medium transition-colors text-sm"
                        >
                          <Link2 className="w-4 h-4" /> Ligar a Sucursal y Caja
                        </button>
                      )}

                      {/* Download button */}
                      {dl && (
                        <a
                          href={dl.download_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium transition-colors text-sm"
                        >
                          <Download className="w-4 h-4" />
                          Descargar {POS_TYPE_LABELS[lic.pos_type]} v{dl.version}
                          {dl.file_size_mb && <span className="text-slate-400 ml-1">({dl.file_size_mb} MB)</span>}
                        </a>
                      )}

                      {/* Re-link for already linked */}
                      {lic.status === 'linked' && (
                        <button
                          onClick={() => { setShowLinkModal(lic); setLinkForm({ branch_id: '', terminal_id: '' }); setTerminals([]); }}
                          className="flex items-center justify-center gap-2 px-4 py-2 text-slate-500 hover:text-slate-700 text-sm transition-colors"
                        >
                          <Link2 className="w-4 h-4" /> Cambiar sucursal/caja
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="px-5 py-3 bg-slate-50 border-t border-slate-100">
                    <p className="text-xs text-slate-400">
                      {lic.notes && <span>{lic.notes} | </span>}
                      Creada: {formatDate(lic.created_at)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Link Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-slate-900">Ligar Licencia</h2>
              <button onClick={() => setShowLinkModal(null)} className="text-slate-400 hover:text-slate-600"><X className="w-6 h-6" /></button>
            </div>
            <form onSubmit={handleLink} className="p-6 space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm text-slate-600">Serial: <code className="font-mono font-medium">{showLinkModal.serial_number}</code></p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Sucursal *</label>
                <select
                  required
                  value={linkForm.branch_id}
                  onChange={(e) => handleBranchChange(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  <option value="">Selecciona una sucursal</option>
                  {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Caja POS *</label>
                <select
                  required
                  value={linkForm.terminal_id}
                  onChange={(e) => setLinkForm({ ...linkForm, terminal_id: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  disabled={!linkForm.branch_id}
                >
                  <option value="">{linkForm.branch_id ? 'Selecciona una caja' : 'Primero selecciona sucursal'}</option>
                  {terminals.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">Al ligar la licencia, el POS de escritorio podrá acceder a los productos, inventario y registrar ventas de esta sucursal y caja.</p>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="submit" className="flex-1 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 font-medium transition-colors">Ligar</button>
                <button type="button" onClick={() => setShowLinkModal(null)} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium transition-colors">Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default LicenciasPOS;
